import React, { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { borderStyle, cardStyle, contentBoxStyle } from "../shared/Theme";
import Sidebar from "../elements/Sidebar";
import Header from "../elements/Header";
import darkWebService from "../services/darkWebService";
import BreachLineGraph from "../elements/DarkWeb/BreachLineGraph";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DarkWebOverviewData from "../elements/DarkWeb/DarkWebOverviewData";
import DomainDateSelectModal from "../elements/DarkWeb/DomainDateSelectModal";
import { DateRange, FiberManualRecord } from "@mui/icons-material";
import BreachedCredentialsModal from "../elements/DarkWeb/BreachedCredentialsModal";
import { format } from "date-fns";
import BreachesDiscoveredModal from "../elements/DarkWeb/BreachesDiscoveredModal.jsx";
import IndividualBreachedCredentialsModal from "../elements/DarkWeb/IndividualBreachedCredentialsModal";
import { useEffect } from "react";
import IndividualBreachDiscoveredModal from "../elements/DarkWeb/IndividualBreachDiscoveredModal";
import { truncateString } from "../shared/sharedFunctions";
import AccountGuard from "../elements/Admin/AccountGuard";

const DarkWebMonitoring = () => {
	
	let [ fromDate, setFromDate ] = useState(darkWebService.getMinBreachDate());
	let [ toDate, setToDate ] = useState(darkWebService.getMaxBreachDate());
	let [ breaches, setBreaches ] = useState([]);
	let [ breachOverviews, setBreachOverviews ] = useState(null);
	let [ totals, setTotals ] = useState(darkWebService.getTotals(fromDate, toDate));
	let [ showDomainDateSelect, setShowDomainDateSelect ] = useState(false);
	let [ showCredentialsModal, setShowCredentialsModal ] = useState(false);
	let [ showIndividualBreachModal, setShowIndividualBreachModal ] = useState(false);
	let [ showDiscoveredModal, setShowDiscoveredModal ] = useState(false);
	let [ showIndividualBreachDiscoveredModal, setShowIndividualBreachDiscoveredModal ] = useState(false);
	
	useEffect(() => {
		const discoveredBreaches = {};
		for (let i in breaches) {
			const key = `${breaches[i].name}_${breaches[i].breach_date}`;
			if (!discoveredBreaches.hasOwnProperty(key)) {
				discoveredBreaches[key] = {
					key: key,
					name: breaches[i].name,
					date: breaches[i].breach_date,
					count: 0,
					compromisedDatas: []
				};
			}
			discoveredBreaches[key].count += 1;
			const compromisedDataItems = breaches[i].compromised_data.split(",");
			for (let i in compromisedDataItems) {
				if (discoveredBreaches[key].compromisedDatas.indexOf(compromisedDataItems[i]) === -1) {
					discoveredBreaches[key].compromisedDatas.push(compromisedDataItems[i]);
				}
			}
		}
		
		const breachOverviews = Object.values(discoveredBreaches).sort((a, b) => {
			if (a.breach_date > b.breach_date) return -1;
			else if (a.breach_date < b.breach_date) return 1;
			else return 0;
		});
		
		setBreachOverviews(breachOverviews);
	}, [ breaches ]);
	
	useEffect(() => {
		const newFromDate = darkWebService.getMinBreachDate();
		const newToDate = darkWebService.getMaxBreachDate();
		
		setFromDate(newFromDate);
		setToDate(newToDate);
		setBreaches(darkWebService.BreachesForDates(newFromDate, newToDate));
		setTotals(darkWebService.getTotals(newFromDate, newToDate));
	}, []);
	
	const handleDomainSelected = (domain) => {
		const newFromDate = darkWebService.getMinBreachDate();
		const newToDate = darkWebService.getMaxBreachDate();
		
		setFromDate(newFromDate);
		setToDate(newToDate);
		setBreaches(darkWebService.BreachesForDates(newFromDate, newToDate));
		setTotals(darkWebService.getTotals(newFromDate, newToDate));
	};
	
	const handleDateChanges = (fromDate, toDate) => {
		setFromDate(fromDate);
		setToDate(toDate);
		setBreaches(darkWebService.BreachesForDates(fromDate, toDate));
		setTotals(darkWebService.getTotals(fromDate, toDate));
	};
	
	const getOrderedBreaches = () => {
		let orderedBreaches = [...breaches];
		orderedBreaches.sort((a, b) => {
			if (a.breach_date > b.breach_date) return -1;
			else if (a.breach_date < b.breach_date) return 1;
			else return 0;
		});
		return orderedBreaches;
	};

	const handleDomainDateSelectClick = () => {
		setShowDomainDateSelect(true);
	};

	const handleDomainDateSelectClose = () => {
		setShowDomainDateSelect(false);
	};

	const handleCredentialsModalClick = () => {
		setShowCredentialsModal(true);
	};

	const handleCredentialsModalClose = () => {
		setShowCredentialsModal(false);
	};

	const handleIndividualBreachModalClick = (breach) => {
		setShowIndividualBreachModal(breach);
	};

	const handleIndividualBreachModalClose = () => {
		setShowIndividualBreachModal(null);
	};

	const handleIndividualBreachDiscoveredModalClick = (breach) => {
		setShowIndividualBreachDiscoveredModal(breach);
	};

	const handleIndividualBreachDiscoveredModalClose = () => {
		setShowIndividualBreachDiscoveredModal(null);
	};

	const handleDiscoveredModalClick = () => {
		setShowDiscoveredModal(true);
	};

	const handleDiscoveredModalClose = () => {
		setShowDiscoveredModal(false);
	};
	
	return (
		<Box sx={{ display: "flex" }}>
			<Sidebar/>
			<AccountGuard requiredService="breachaware"/>
			<Box component="main" sx={{flexGrow: 1, bgcolor: "background.default"}}>
				<Header pageTitle="Dark Web Monitoring"/>
				<Box sx={contentBoxStyle}>
					<Stack sx={{ width: "80%", margin: "auto" }} justifyContent="center">
						<Box sx={{ width: "100%", borderBottom: "solid 2px", ...borderStyle }}>
							<Box sx={{ ...cardStyle }}>
								<Box sx={{ width: "100%" }}>
									<Typography variant="h5" sx={{
										width: "100%",
										textAlign: "center",
										fontWeight: "bold",
										paddingTop: "0.5em"
									}}>
										Compromised Data
									</Typography>
									<DateRange
										onClick={handleDomainDateSelectClick}
										sx={{
											position: "relative",
											bottom: "1.1em",
											left: "97%",
											"&:hover": { cursor: "pointer" }
										}}
									/>
								</Box>
								<DarkWebOverviewData
									totals={totals}
									handleShowCredentials={handleCredentialsModalClick}
									handleShowBreaches={handleDiscoveredModalClick}
								/>
							</Box>
						</Box>
						<Box sx={{ width: "100%", borderBottom: "solid 2px", ...borderStyle }}>
							<Box sx={{ ...cardStyle }}>
								<Typography
									variant="h5"
									sx={{ width: "100%", textAlign: "center", fontWeight: "bold", paddingTop: "0.5em" }}
								>
									Breach Timeline
								</Typography>
								<BreachLineGraph
									breaches={breaches}
									fromDate={fromDate}
									toDate={toDate}
								/>
							</Box>
						</Box>
						<Stack direction="row" sx={{ width: "100%" }}>
							<Box sx={{ width: "50%", borderRight: "solid 2px", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{ width: "100%", textAlign: "center", fontWeight: "bold", marginTop: "1em" }}
									>
										Exposed Credentials List
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "1.25em",
										}}
									>
										These are the Exposed Corporate Credentials discovered in<br/>
										available and visible Dark Web Data sets that have been<br/>
										identified as belonging to your Organisation.
									</Typography>
									<Stack direction="row" justifyContent="center" alignItems="center">
										<Box
											justifyContent="center"
											sx={{
												width: "35%",
												height: "4em",
												border: 2,
												margin: "0.25em auto",
												borderRadius: "2em",
												borderColor: "#ff03ed",
												textAlign: "center",
												padding: "0.5em",
												"&:hover": { cursor: "pointer" }
											}}
											onClick={handleCredentialsModalClick}
										>
											<Typography sx={{ marginTop: "0.65em" }}>
												Full List
											</Typography>
										</Box>
										<Stack direction="column" sx={{ width: "50%",  marginBottom: "2em" }}>
											{getOrderedBreaches().map((breach, i) => {
												if (i >= 4) return ( <React.Fragment key={`breach_list_item_${i}`}/> );
												return (
													<Stack
														key={`breach_list_item_${i}`}
														direction="row"
														alignItems="center"
														justifyContent="left"
														sx={{ "&:hover": { cursor: "pointer" }, margin: "0" }}
														onClick={() => {handleIndividualBreachModalClick(breach)}}
													>
														<FiberManualRecord sx={{ width: "0.3em", height: "0.3em" }}/>
														<Typography sx={{ width: "60%", margin: "0.5em" }}>
															{`${truncateString(breach.email, 18)}`}
														</Typography>
														<ArrowBackIosNewIcon
															sx={{ transform: "rotate(270deg)", color: "#ff03ed" }}
														/>
													</Stack>
												);
											})}
										</Stack>
									</Stack>
								</Stack>
							</Box>
							<Box sx={{ width: "50%", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{ width: "100%", textAlign: "center", fontWeight: "bold", marginTop: "1em" }}
									>
										Breaches Discovered
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "1.25em",
										}}
									>
										These Breaches have been identified and verified as containing<br/>
										Assets from your Organisations. Here we provide details on the actual<br/>
										Breaches themselves to provide context.
									</Typography>
									<Stack direction="row" justifyContent="center" alignItems="center">
										<Box
											justifyContent="center"
											sx={{
												width: "35%",
												height: "4em",
												border: 2,
												margin: "0.25em auto",
												borderRadius: "2em",
												borderColor: "#ff03ed",
												textAlign: "center",
												padding: "0.5em",
												"&:hover": { cursor: "pointer" }
											}}
											onClick={handleDiscoveredModalClick}
										>
											<Typography sx={{ marginTop: "0.65em" }}>
												Full List
											</Typography>
										</Box>
										{breachOverviews?
											<Stack direction="column" sx={{ width: "50%", marginBottom: "2em" }}>
												{breachOverviews.map((breachOverview, i) => {
													if (i >= 4) return ( <React.Fragment key={`breach_overview_${i}`}/> );
													return (
														<Stack
															key={`breach_overview_${i}`}
															direction="row"
															alignItems="center"
															justifyContent="left"
															sx={{ "&:hover": { cursor: "pointer" }, margin: "0" }}
															onClick={() => {
																handleIndividualBreachDiscoveredModalClick(breachOverview)
															}}
														>
															<FiberManualRecord sx={{ width: "0.3em", height: "0.3em" }}/>
															<Typography sx={{ width: "60%", margin: "0.5em" }}>
																<span style={{ fontWeight: "bold" }}>
																	{`${truncateString(breachOverview.name, 30)}`}
																</span>
																{` ${format(breachOverview.date, "MM-yyyy")}`}
															</Typography>
															<ArrowBackIosNewIcon
																sx={{ transform: "rotate(270deg)", color: "#ff03ed" }}
															/>
														</Stack>
													);
												})}
											</Stack>
										: <React.Fragment/>}
									</Stack>
								</Stack>
							</Box>
						</Stack>
					</Stack>
				</Box>
				<DomainDateSelectModal
					open={showDomainDateSelect}
					onClose={handleDomainDateSelectClose}
					onDomainSelected={handleDomainSelected}
					onDateChanges={handleDateChanges}
					fromDate={fromDate}
					toDate={toDate}
				/>
				<BreachedCredentialsModal
					open={showCredentialsModal}
					onClose={handleCredentialsModalClose}
					breaches={getOrderedBreaches()}
				/>
				<BreachesDiscoveredModal
					open={showDiscoveredModal}
					onClose={handleDiscoveredModalClose}
					breaches={breachOverviews}
				/>
				<IndividualBreachedCredentialsModal
					breach={showIndividualBreachModal}
					onClose={handleIndividualBreachModalClose}
				/>
				<IndividualBreachDiscoveredModal
					breach={showIndividualBreachDiscoveredModal}
					onClose={handleIndividualBreachDiscoveredModalClose}
				/>
			</Box>
		</Box>
	);
}

export default DarkWebMonitoring;
